import { useId } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  Button,
  CopyButton,
  eyebrowClassName,
} from '@ideon-api/ideon-ui'
import { QUARTERS, QUARTER_AUDIENCES, audienceLabel, type Audience, type Periods } from '../../lib/plan-entitlements'
import { productLineLabel } from '../../lib/product-lines'

export type Period = (typeof QUARTERS)[number] | 'Annual'

// 50 states + DC. Codes outside it (PR) are listed but never counted.
export const US_CODES = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' ')

export { audienceLabel }

export const lineKey = (productLine: string, audience: Audience) => `${productLine}.${audience}`
export const isQuarterlyAudience = (audience: Audience) => (QUARTER_AUDIENCES as readonly string[]).includes(audience)
const cadenceOf = (audience: Audience) => (isQuarterlyAudience(audience) ? 'quarterly' : 'annual')

export function lineLabel(productLine: string, audience: Audience) {
  return `${productLineLabel(productLine)} · ${audienceLabel(audience)} · ${cadenceOf(audience)}`
}

// Shared by the header row and every line row so the quarter columns line up.
const ROW_COLUMNS = 'lg:grid lg:grid-cols-[minmax(0,1fr)_24rem_4.5rem] lg:items-center lg:gap-x-4'

const inUniverse = (states: readonly string[]) => new Set(states.map((s) => s.trim().toUpperCase()).filter((s) => US_CODES.includes(s)))

// Count only; "All" past 50 states (DC makes 51). Shared with the history drawer so both agree.
export const countStates = (states: readonly string[]) => inUniverse(states).size

export function countLabel(states: readonly string[]) {
  const n = countStates(states)
  return n > 50 ? 'All' : `${n} ${n === 1 ? 'state' : 'states'}`
}

export function QuarterHeaders() {
  return (
    <div aria-hidden="true" className={`${ROW_COLUMNS} hidden px-4 py-2 lg:grid`}>
      <span />
      <div className={`grid grid-cols-4 gap-2 text-center ${eyebrowClassName} text-ideon-gray-70`}>
        {QUARTERS.map((q) => (
          <span key={q}>{q}</span>
        ))}
      </div>
      <span />
    </div>
  )
}

interface Props {
  productLine: string
  audience: Audience
  year: string
  periods: Periods
  // The period whose panel is open, or null when this line's panel is closed.
  openPeriod: Period | null
  onSelect: (period: Period) => void
  onHistory: () => void
}

export function CoverageLine({ productLine, audience, year, periods, openPeriod, onSelect, onHistory }: Props) {
  const panelId = useId()
  const quarterly = isQuarterlyAudience(audience)
  const openStates = openPeriod ? [...(periods[openPeriod] ?? [])].sort() : []
  const have = inUniverse(openStates)
  const licensedCodes = [...new Set(openStates.map((s) => s.trim().toUpperCase()))].sort()
  const missingCodes = US_CODES.filter((c) => !have.has(c)).sort()

  const button = (period: Period) => {
    const open = openPeriod === period
    const count = countLabel(periods[period] ?? [])
    const Chevron = open ? ChevronDown : ChevronRight
    return (
      <Button
        key={period}
        type="button"
        size="sm"
        variant={open ? 'filled' : 'outline'}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${period}, ${count === 'All' ? 'all states' : count}, ${productLineLabel(productLine)} ${audienceLabel(audience)}`}
        onClick={() => onSelect(period)}
        className={`w-full gap-1 max-lg:h-11 ${period === 'Annual' ? 'justify-between px-3' : 'justify-center px-1.5'}`}
      >
        {period === 'Annual' ? (
          <>
            <span className={eyebrowClassName}>ANNUAL</span>
            <span className="inline-flex items-center gap-1">
              {count}
              <Chevron className="size-4" aria-hidden="true" />
            </span>
          </>
        ) : (
          <>
            <span className="lg:hidden">{period}</span>
            <span className="truncate">{count}</span>
            <Chevron className="size-4 shrink-0" aria-hidden="true" />
          </>
        )}
      </Button>
    )
  }

  return (
    <div className="border-b border-ideon-gray-40 last:border-b-0">
      <div className={`${ROW_COLUMNS} flex flex-col gap-2 px-4 py-3`}>
        <div className="min-w-0">
          <div className="font-semibold text-ideon-gray-black">{productLineLabel(productLine)}</div>
          <div className="text-xs text-ideon-gray-70">{`${audienceLabel(audience)} · ${cadenceOf(audience)}`}</div>
        </div>
        {quarterly ? (
          <div className="grid grid-cols-4 gap-2 max-lg:grid-cols-2">{QUARTERS.map((q) => button(q))}</div>
        ) : (
          button('Annual')
        )}
        <Button
          type="button"
          size="xs"
          variant="text"
          aria-label={`History for ${lineLabel(productLine, audience)}`}
          onClick={onHistory}
          className="justify-self-end max-lg:self-start"
        >
          History
        </Button>
      </div>
      {openPeriod && (
        <div id={panelId} className="flex flex-col gap-3 border-t border-ideon-gray-40 bg-ideon-gray-20 px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium text-ideon-gray-black">{`${openPeriod} · ${year}`}</h3>
            {openStates.length > 0 && <CopyButton value={openStates.join(', ')} label="Copy states" />}
          </div>
          <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-baseline gap-x-4 gap-y-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
            <dt className={`${eyebrowClassName} text-ideon-state-success-dark`}>LICENSED</dt>
            <dd>
              <Codes codes={licensedCodes} className="text-ideon-gray-black" />
            </dd>
            <dt className={`${eyebrowClassName} text-ideon-gray-70`}>NOT LICENSED</dt>
            <dd data-testid="not-licensed-codes">
              <Codes codes={missingCodes} className="text-ideon-gray-70" />
            </dd>
          </dl>
        </div>
      )}
    </div>
  )
}

export function Codes({ codes, className }: { codes: string[]; className: string }) {
  if (!codes.length) return <span className="text-[13px] italic text-ideon-gray-70">none</span>
  return (
    <div className={`break-words font-mono text-[13px] ${className}`}>
      {codes.map((c, i) => (
        <span key={c}>
          {i > 0 ? ' ' : ''}
          {c}
        </span>
      ))}
    </div>
  )
}
